import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Edit2, 
  Trash2, 
  AlertTriangle,
  Plus,
  ShoppingBag, 
  Coffee, 
  Truck, 
  Home, 
  Film, 
  Heart, 
  DollarSign, 
  Smartphone, 
  Zap, 
  Book, 
  Briefcase,
  Target,
  Tool,
  Music,
  Folder
} from 'react-feather';
import { apiClient } from '../../shared/api/client';

interface ChildCategory {
  id: number;
  name: string;
  icon?: string;
  color?: string;
  budget: number;
}

interface Category {
  id: number;
  name: string;
  icon: string;
  color: string;
  budget: number;
  children: ChildCategory[];
}

const CategoriesPage: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: '',
    icon: 'folder',
    color: '#10B981',
    monthlyBudget: 0
  });
  const [editingCategory, setEditingCategory] = useState<{
    id: number;
    name: string;
    icon: string;
    color: string;
    monthlyBudget: number;
  } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading categories...');
      const response = await apiClient.get('/categories');
      console.log('Categories response:', response);
      
      // The response structure is { success: true, categories: [...] }
      // but apiClient wraps it in { success: true, data: { success: true, categories: [...] } }
      const categoriesData = (response as any).categories || response.data?.categories || [];
      console.log('Categories data:', categoriesData);
      
      if (categoriesData.length > 0 || response.success) {
        setCategories(categoriesData);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Load categories error:', error);
      setError('Kunde inte ansluta till servern. Kontrollera din internetanslutning och försök igen.');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      alert('Kategorinamn krävs');
      return;
    }

    setSaving(true);
    try {
      console.log('Creating category:', newCategory);
      const response = await apiClient.post('/categories', {
        name: newCategory.name,
        icon: newCategory.icon,
        color: newCategory.color,
        monthlyBudget: newCategory.monthlyBudget
      });

      console.log('Create category response:', response);

      if (response.success) {
        console.log('Category created successfully, reloading...');
        setShowAddModal(false);
        setNewCategory({
          name: '',
          icon: 'folder',
          color: '#10B981',
          monthlyBudget: 0
        });
        // Reload categories
        await loadCategories();
      } else {
        alert(response.error || 'Kunde inte skapa kategori');
      }
    } catch (error) {
      console.error('Add category error:', error);
      alert('Kunde inte skapa kategori');
    } finally {
      setSaving(false);
    }
  };

  const availableIcons = [
    { name: 'folder', component: Folder },
    { name: 'coffee', component: Coffee },
    { name: 'truck', component: Truck },
    { name: 'home', component: Home },
    { name: 'film', component: Film },
    { name: 'heart', component: Heart },
    { name: 'shopping-bag', component: ShoppingBag },
    { name: 'dollar-sign', component: DollarSign },
    { name: 'smartphone', component: Smartphone },
    { name: 'zap', component: Zap },
    { name: 'book', component: Book },
    { name: 'briefcase', component: Briefcase },
    { name: 'target', component: Target },
    { name: 'tool', component: Tool },
    { name: 'music', component: Music }
  ];

  const getIconComponent = (iconName?: string) => {
    if (!iconName) return Folder;
    const icon = availableIcons.find(i => i.name === iconName);
    return icon ? icon.component : Folder;
  };

  const handleEditCategory = (category: Category | ChildCategory) => {
    setEditingCategory({
      id: category.id,
      name: category.name,
      icon: category.icon || 'folder',
      color: category.color || '#10B981',
      monthlyBudget: category.budget
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingCategory || !editingCategory.name.trim()) {
      alert('Kategorinamn krävs');
      return;
    }

    setSaving(true);
    try {
      console.log('Updating category:', editingCategory);
      const response = await apiClient.put(`/categories/${editingCategory.id}`, {
        name: editingCategory.name,
        icon: editingCategory.icon,
        color: editingCategory.color,
        monthlyBudget: editingCategory.monthlyBudget
      });

      console.log('Update category response:', response);

      if (response.success) {
        console.log('Category updated successfully, reloading...');
        setShowEditModal(false);
        setEditingCategory(null);
        await loadCategories();
      } else {
        alert(response.error || 'Kunde inte uppdatera kategori');
      }
    } catch (error) {
      console.error('Update category error:', error);
      alert('Kunde inte uppdatera kategori');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (categoryId: number, categoryName: string) => {
    if (!window.confirm(`Är du säker på att du vill ta bort kategorin "${categoryName}"? Detta kan inte ångras.`)) {
      return;
    }

    try {
      const response = await apiClient.delete(`/categories/${categoryId}`);
      
      if (response.success) {
        console.log('Category deleted, reloading...');
        await loadCategories();
      } else {
        alert(response.error || 'Kunde inte ta bort kategori');
      }
    } catch (error) {
      console.error('Delete category error:', error);
      alert('Kunde inte ta bort kategori');
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const renderCategory = (category: Category) => {
    const IconComponent = getIconComponent(category.icon);
    
    return (
      <div key={category.id} className="bg-white rounded-xl shadow-sm overflow-hidden mb-4">
        {/* Parent Category */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <div className="w-8 h-8 flex items-center justify-center mr-3 text-gray-900">
                <IconComponent size={20} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
            </div>
          <div className="flex items-center gap-2">
            <span className="text-base font-medium text-gray-900">
              {formatAmount(category.budget)}
            </span>
            <button 
              onClick={() => handleEditCategory(category)}
              className="text-gray-900 hover:text-gray-600 transition-colors p-2"
              title="Redigera kategori"
            >
              <Edit2 size={18} />
            </button>
            <button 
              onClick={() => handleDeleteCategory(category.id, category.name)}
              className="text-gray-900 hover:text-gray-600 transition-colors p-2"
              title="Ta bort kategori"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Child Categories */}
      {category.children.map((child) => (
        <div key={child.id} className="pl-12 pr-4 py-3 border-b border-gray-50 last:border-b-0">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <h4 className="text-base text-gray-900">{child.name}</h4>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">
                {formatAmount(child.budget)}
              </span>
              <button 
                onClick={() => handleEditCategory(child)}
                className="text-gray-900 hover:text-gray-600 transition-colors p-2"
                title="Redigera underkategori"
              >
                <Edit2 size={16} />
              </button>
              <button 
                onClick={() => handleDeleteCategory(child.id, child.name)}
                className="text-gray-900 hover:text-gray-600 transition-colors p-2"
                title="Ta bort underkategori"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
    );
  };

  // Calculate totals
  const totalBudget = categories.reduce((sum, category) => {
    return sum + category.budget;
  }, 0);

  const totalSubcategories = categories.reduce((sum, category) => {
    return sum + category.children.length;
  }, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="page-header bg-white shadow-sm border-b border-gray-200">
          <div className="page-header-content px-4 py-4">
            <div className="flex items-center">
              <button onClick={() => navigate('/settings')} className="mr-4">
                <span className="text-xl">‹</span>
              </button>
              <h1 className="text-xl font-bold text-gray-900">Kategorier</h1>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Laddar kategorier...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="page-header bg-white shadow-sm border-b border-gray-200">
          <div className="page-header-content px-4 py-4">
            <div className="flex items-center">
              <button onClick={() => navigate('/settings')} className="mr-4">
                <span className="text-xl">‹</span>
              </button>
              <h1 className="text-xl font-bold text-gray-900">Kategorier</h1>
            </div>
          </div>
        </div>
        <div className="page-content px-4 py-6">
          <div className="bg-white rounded-xl p-6 shadow-sm text-center">
            <AlertTriangle size={48} className="mx-auto mb-4 text-red-400" />
            <h2 className="text-xl font-semibold text-red-600 mb-2">Kunde inte ladda kategorier</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={loadCategories}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Försök igen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="page-header bg-white shadow-sm border-b border-gray-200">
        <div className="page-header-content px-4 py-4">
          <div className="flex items-center">
            <button onClick={() => navigate('/settings')} className="mr-4">
              <span className="text-xl">‹</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">Kategorier</h1>
          </div>
        </div>
      </div>

      <div className="page-content px-4 py-6">
        <p className="text-gray-600 mb-6">Hantera dina utgiftskategorier och budgetar</p>
        
        {/* Summary Stats */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-xl font-bold text-blue-600 mb-1">{categories.length}</p>
              <p className="text-xs text-gray-600">Huvudkategorier</p>
            </div>
            <div>
              <p className="text-xl font-bold text-blue-600 mb-1">{totalSubcategories}</p>
              <p className="text-xs text-gray-600">Underkategorier</p>
            </div>
            <div>
              <p className="text-xl font-bold text-blue-600 mb-1">{formatAmount(totalBudget)}</p>
              <p className="text-xs text-gray-600">Total budget</p>
            </div>
          </div>
        </div>

        {/* Kategorier */}
        <div className="mb-6">
          {categories.length > 0 ? (
            categories.map(renderCategory)
          ) : (
            <div className="text-center py-12 bg-white rounded-xl">
              <Folder size={48} className="mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Inga kategorier ännu</h3>
              <p className="text-gray-600">Skapa dina första kategorier för att organisera dina utgifter</p>
            </div>
          )}
        </div>

        {/* Add Category Button */}
        <button 
          onClick={() => setShowAddModal(true)}
          className="w-full bg-white border-2 border-dashed border-blue-600 text-blue-600 py-4 rounded-xl font-semibold hover:bg-blue-50 transition-colors"
        >
          <Plus size={16} className="inline mr-1 -mt-0.5" />Lägg till kategori
        </button>

        {/* Placeholder för kommande funktioner */}
        
      </div>

      {/* Edit Category Modal */}
      {showEditModal && editingCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Redigera kategori</h2>

            {/* Category Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Kategorinamn</label>
              <input
                type="text"
                value={editingCategory.name}
                onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="T.ex. Mat & Dryck"
              />
            </div>

            {/* Icon Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Välj ikon</label>
              <div className="grid grid-cols-5 gap-2">
                {availableIcons.map(icon => {
                  const IconComp = icon.component;
                  return (
                    <button
                      key={icon.name}
                      onClick={() => setEditingCategory({ ...editingCategory, icon: icon.name })}
                      className={`p-3 rounded-lg border-2 transition-colors flex items-center justify-center ${
                        editingCategory.icon === icon.name
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <IconComp size={20} />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Color Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Välj färg</label>
              <div className="grid grid-cols-6 gap-2">
                {['#10B981', '#3B82F6', '#8B5CF6', '#FF6B35', '#EC4899', '#F59E0B', '#EF4444', '#14B8A6', '#6366F1', '#F97316', '#84CC16', '#06B6D4'].map(color => (
                  <button
                    key={color}
                    onClick={() => setEditingCategory({ ...editingCategory, color })}
                    className={`w-10 h-10 rounded-lg border-2 transition-all ${
                      editingCategory.color === color
                        ? 'border-gray-900 scale-110'
                        : 'border-gray-200'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            {/* Monthly Budget */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Månadsbudget (kr)</label>
              <input
                type="number"
                value={editingCategory.monthlyBudget}
                onChange={(e) => setEditingCategory({ ...editingCategory, monthlyBudget: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0"
                min="0"
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingCategory(null);
                }}
                className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Sparar...' : 'Spara'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Lägg till kategori</h2>

            {/* Category Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Kategorinamn</label>
              <input
                type="text"
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="T.ex. Mat & Dryck"
              />
            </div>

            {/* Icon Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Välj ikon</label>
              <div className="grid grid-cols-5 gap-2">
                {availableIcons.map(icon => {
                  const IconComp = icon.component;
                  return (
                    <button
                      key={icon.name}
                      onClick={() => setNewCategory({ ...newCategory, icon: icon.name })}
                      className={`p-3 rounded-lg border-2 transition-colors flex items-center justify-center ${
                        newCategory.icon === icon.name
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <IconComp size={20} />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Color Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Välj färg</label>
              <div className="grid grid-cols-6 gap-2">
                {['#10B981', '#3B82F6', '#8B5CF6', '#FF6B35', '#EC4899', '#F59E0B', '#EF4444', '#14B8A6', '#6366F1', '#F97316', '#84CC16', '#06B6D4'].map(color => (
                  <button
                    key={color}
                    onClick={() => setNewCategory({ ...newCategory, color })}
                    className={`w-10 h-10 rounded-lg border-2 transition-all ${
                      newCategory.color === color
                        ? 'border-gray-900 scale-110'
                        : 'border-gray-200'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            {/* Monthly Budget */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Månadsbudget (kr)</label>
              <input
                type="number"
                value={newCategory.monthlyBudget}
                onChange={(e) => setNewCategory({ ...newCategory, monthlyBudget: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0"
                min="0"
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleAddCategory}
                disabled={saving}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Sparar...' : 'Lägg till'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoriesPage;


